import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { isEntity, isProperty } from "../../../data/utils";
import FilterCtrl from "../FilterCtrl";
import MeasurePropertyCtrl from "../MeasurePropertyCtrl";
import { SpatialEntity } from "../SpatialEntity";
import { scales } from'../../../colorscaleUtils';
import { IColorStyle, IFilter, IItem, IMeasure } from '../../../data/Query';
import { Definition, Entity } from "../../../data/Definition";
import ColorSelector from "../../ColorSelector";

type ScatterChartFormProps = {
    definition: Definition,
    chartConfig: any | null,
    saveConfig: Function
};

const ScatterConfigForm = (props:ScatterChartFormProps) => {
    const configVersion = '2';

    const [measureX, setMeasureX] = useState<IMeasure | null>(null);
    const [measureY, setMeasureY] = useState<IMeasure | null>(null);
    const [measureColor, setMeasureColor] = useState<IMeasure | null>(null);
    const [spatial, setSpatial] = useState<IItem | null>(null);
    const [filters, setFilters] = useState<IFilter[]>([]);
    const [colorStyle, setColorStyle] = useState<IColorStyle | null>(null);
    const [isValid, setIsValid] = useState<boolean>(false);

    const colorScaleNames = Object.keys(scales);

    useEffect(() => {
        if(props.chartConfig) {
            initFromConfig(props.chartConfig);
        } 
    }, []);

    useEffect(() => {
        console.log('measureX', measureX, measureY);
        setIsValid(measureX != null && measureX.source.item != null && measureY != null && measureY.source.item != null);
    }, [measureX, measureY])

    const initFromConfig = (config: any) => {
        // Sending the object through the socket io removes class types.
        // Get the type entity or property from the definition. The types
        // are used in the components.
        //
        console.log('scatter chart > initFromConfig', config, props.definition);

        // measures
        if(config.measure) {
            let measures: IMeasure[] = config.measure.map((m: IMeasure) => {
                return {
                    source: {
                        item: m.source.item ? getItemFromDefinition(m.source.item) : null,
                        sub: m.source.sub
                    }
                }
            });

            setMeasureX(measures[0]);
            setMeasureY(measures[1]);

            if(measures[2]) {
                setMeasureColor(measures[2]);
            }
        }

        // color measure
        if(config.color) {
            setMeasureColor({
                source: {
                    item: config.color.source.item ? getItemFromDefinition(config.color.source.item) : null,
                    sub: config.color.source.sub
                }
            });
        }

        if(config.colorStyle) {
            setColorStyle(config.colorStyle);
        }

        // filters
        // there is a chance that a filter has not been configured and contains an
        // empty item. remove those filters.
        let filters = config.filters.map((filter:IFilter) : IFilter => {
            const f: IFilter = {
                source: {
                    item: getItemFromDefinition(filter.source.item),
                    sub: filter.source.sub
                },
                operation: filter.operation,
                value: filter.value
            };

            return f;
        });

        setFilters(filters);

        // spatial
        let spatial: IItem = {
            item: config.spatial != null && config.spatial.item ? getItemFromDefinition(config.spatial.item) : null,
            sub: null
        };

        setSpatial(spatial);
    }

    const saveToConfig = (e: any) => {  
        e.preventDefault();

        let measures = [measureX, measureY];
        
        if(measureColor) {
            measures.push(measureColor);
        }

        let config = {
            version: configVersion,
            measure: measures,
            color: measureColor && measureColor.source.item ? measureColor : null,
            colorStyle: colorStyle,
            spatial: spatial,
            filters: filters 
        };

        console.log('saveToConfig', config);

        // pass back to caller.
        if(props.saveConfig) {
            props.saveConfig(config);
        }
    }

    const getItemFromDefinition = (item:any) => {   
        let defItem = item;

        // main entity.
        if(item.name === props.definition.name) {
            return props.definition as Entity;
        }

        if(isEntity(item)) {
            defItem = props.definition.entity.find((i:any) => {
                return i.name === item.name;
            });
        } else if(isProperty(item)) {
            defItem = props.definition.property.find((i:any) => {
                return i.name === item.name;
            });
        }

        return defItem;
    }

    return (
        <div>
            <div className="mt-3 p-3 bg-dark">
                <MeasurePropertyCtrl
                    title='X Measure'
                    definition={props.definition}
                    measure={measureX}
                    onUpdate={(f: any) => setMeasureX(f)} />

                <MeasurePropertyCtrl
                    title='Y Measure'
                    definition={props.definition}
                    measure={measureY}
                    onUpdate={(f: any) => setMeasureY(f)} />

                <MeasurePropertyCtrl
                    title='Color Measure'
                    definition={props.definition}
                    measure={measureColor}
                    onUpdate={(f: any) => setMeasureColor(f)} />
            </div>
            <div className="mt-3 p-3 bg-dark">
                <Form.Group>
                    <Form.Label>Color Scale</Form.Label>
                    <ColorSelector config={colorStyle == null ? {} as IColorStyle : colorStyle} onUpdate={(e: IColorStyle) => setColorStyle(e)}></ColorSelector>
                </Form.Group>
            </div>
            <div className="mt-3 p-3 bg-dark">
                <SpatialEntity
                    definition={props.definition}
                    item={spatial}
                    onUpdate={(item: IItem) => setSpatial(item)}
                />

                <FilterCtrl
                    definition={props.definition}
                    config={filters}
                    onUpdate={(f: any) => setFilters(f)} />
            </div>
            <Button
                className="mt-3"
                variant="primary"
                type="submit"
                disabled={!isValid}
                onClick={saveToConfig}>
                Apply
            </Button>
        </div>
    );
}

export default ScatterConfigForm;